import React, { useMemo, useCallback } from 'react';
import { CommerceNoteSearchParams, EntityWithNotes, CommerceNote } from '@atlassian/cssxp-types-audit';
import CommerceNotesList from './commerce-notes-list';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { fetchCommerceNotes, searchCommerceNotesQuotes, searchCommerceNotesRefunds } from '@atlassian/temp-proxies';

import { useSearch } from '@css-xp/base';

export interface CommerceNoteComponentProps {
  adaUrl: string;
  params: CommerceNoteSearchParams;
  entity: EntityWithNotes;
  NotesView?: React.ComponentType<React.ComponentProps<typeof CommerceNotesList> & Record<string, unknown>>;
  NotesViewProps?: Record<string, unknown>;
  sourceSystem?: SourceSystem | null;
  id?: string;
}

export type Props = CommerceNoteComponentProps;

function CommerceNoteSearch(props: Props) {
  const { adaUrl, params, sourceSystem } = props;

  const updatedParams = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const { entitlementKey, ...paramsWithoutEntitlementKey } = params;
    return {
      sort: [{ created: 'desc' }],
      ...paramsWithoutEntitlementKey,
    };
  }, [params]);

  const fetchSearchResults = useCallback(
    (searchParams) => {
      return fetchCommerceNotes(params.entitlementKey ?? '', searchParams);
    },
    [params.entitlementKey]
  );

  const {
    isLoadMoreEnabled: showLoadMore,
    isSearching: isSearching,
    searchResults: notes,
    totalHits,
    error: error,
    handleLoadMore,
  } = useSearch<CommerceNote>({
    fetchSearchResults,
    params: updatedParams,
  });

  // create the properties that should always be passed to the child node
  const defaultNotesViewProps = {
    adaUrl,
    error: error,
    isSearching: isSearching,
    showLoadMore: showLoadMore,
    notes,
    onLoadMoreClick: handleLoadMore,
    totalHits,
    sourceSystem: sourceSystem || SourceSystem.CCP,
  };
  return (
    // Use the default CommerceNotesList if no children are passed in
    // This will allow future states where custom views are needed
    // CommerceNotesList should not be set as a default value to NotesView as custom props could be passed
    <CommerceNotesList {...defaultNotesViewProps} />
  );
}

export function CommerceNoteSearchQuotes(props: Props) {
  const { adaUrl, entity, params, NotesView, NotesViewProps, sourceSystem } = props;
  const updatedParams = useMemo(() => {
    return {
      sort: [{ created: 'desc' }],
      ...params,
    };
  }, [params]);

  const {
    isLoadMoreEnabled: showLoadMore,
    isSearching,
    searchResults: notes,
    totalHits,
    error,
    handleLoadMore,
  } = useSearch<CommerceNote>({
    fetchSearchResults: (searchParams) => {
      if (searchParams) {
        return searchCommerceNotesQuotes(searchParams);
      }

      return Promise.resolve({
        totalHits: 0,
        results: [],
      });
    },
    params: updatedParams,
  });

  // create the properties that should always be passed to the child node
  const defaultNotesViewProps = {
    adaUrl,
    error,
    isSearching,
    showLoadMore,
    notes,
    onLoadMoreClick: handleLoadMore,
    totalHits,
    sourceSystem,
  };

  return (
    // Use the default CommerceNotesList if no children are passed in
    // This will allow future states where custom views are needed
    // CommerceNotesList should not be set as a default value to NotesView as custom props could be passed (
    <CommerceNotesList {...defaultNotesViewProps} />
  );
}

export function CommerceNoteSearchRefunds(props: Props) {
  const { adaUrl, entity, params, NotesView, NotesViewProps, sourceSystem, id } = props;
  const updatedParams = useMemo(() => {
    return {
      sort: [{ created: 'desc' }],
      ...params,
    };
  }, [params]);

  const {
    isLoadMoreEnabled: showLoadMore,
    isSearching,
    searchResults: notes,
    totalHits,
    error,
    handleLoadMore,
  } = useSearch<CommerceNote>({
    fetchSearchResults: (searchParams) => {
      if (searchParams) {
        //@ts-ignore
        return searchCommerceNotesRefunds(id, searchParams);
      }

      return Promise.resolve({
        totalHits: 0,
        results: [],
      });
    },
    params: updatedParams,
  });

  // create the properties that should always be passed to the child node
  const defaultNotesViewProps = {
    adaUrl,
    error,
    isSearching,
    showLoadMore,
    notes,
    onLoadMoreClick: handleLoadMore,
    totalHits,
    sourceSystem,
  };

  return (
    // Use the default CommerceNotesList if no children are passed in
    // This will allow future states where custom views are needed
    // CommerceNotesList should not be set as a default value to NotesView as custom props could be passed (
    //@ts-ignore
    <CommerceNotesList {...defaultNotesViewProps} />
  );
}
export default CommerceNoteSearch;
